import { X } from "lucide-react";

import type { SentMessage } from "./types";

/**
 * Messages lined up behind the running turn: dimmed user bubbles at the tail of
 * the transcript, right where they'll land once they're sent.
 *
 * They belong IN the transcript, not in a strip above the composer. A host with
 * an optimistic frontend queues on every send — the bubble appears the instant
 * the member hits enter and clears when the turn takes it — so a separate panel
 * would flash into existence and out again on every single message. As a dimmed
 * bubble in place, the same event reads as the message simply arriving.
 *
 * Two hosts, two meanings, one component. A queued send may be **removable** —
 * the host is holding it client-side and hasn't sent it — or not, when it has
 * already been POSTed and only the server can decide its fate. That is the whole
 * difference, so it's one optional callback rather than two components.
 */
export function QueuedMessages({
  queued,
  onRemove,
}: {
  queued: SentMessage[];
  /** Drop the message at `index`. Omit when the sends are already committed. */
  onRemove?: (index: number) => void;
}) {
  return (
    <>
      {queued.map((message, index) =>
        // A hidden message is for the model, not the member — it must not
        // appear here any more than it appears in the transcript.
        message.hidden ? null : (
          <li
            key={index}
            className="group flex max-w-[80%] items-start gap-2 self-end rounded-2xl bg-primary/60 px-4 py-2 text-sm text-primary-foreground"
            aria-label="Sending message"
          >
            <span className="min-w-0 whitespace-pre-wrap break-words">
              {label(message)}
            </span>
            {onRemove ? (
              <button
                type="button"
                aria-label="Remove queued message"
                title="Remove queued message"
                onClick={() => onRemove(index)}
                className="-mr-1 shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-primary-foreground/20 group-hover:opacity-100 focus-visible:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </li>
        ),
      )}
    </>
  );
}

/** A text-free send still needs something to read in its bubble. */
function label(message: SentMessage): string {
  if (message.text) return message.text;
  return message.files.length > 0 ? "Attachment" : "Message";
}
